import { type FC, type ReactNode, useId } from "react";

import {
  Box,
  Column,
  Heading,
  type HeadingProps,
  Row,
  Text,
  Tooltip,
} from "@hightouch/htui";
import { formatDistanceToNow } from "date-fns";

import { ToggleButton } from "src/ui/toggle-button";
import { formatDateLong, formatTime } from "src/utils/time";

import { barColor, getDisplayNameForResource } from "./resource-display";

const VALUE_HEADING_SIZES: Record<"md" | "lg", HeadingProps["size"]> = {
  lg: "2xl",
  md: "xl",
};

type Props = {
  directDependents: number;
  indirectDependents: number;
  countsByType: ReadonlyMap<string, number>;
  selectedTypes: ReadonlySet<string>;
  onToggleType: (resourceType: string) => void;
  lastCalculatedAt: Date | null;
};

export const UsageSummary: FC<Props> = ({
  directDependents,
  indirectDependents,
  countsByType,
  selectedTypes,
  onToggleType,
  lastCalculatedAt,
}) => {
  const typeFilterLabelId = useId();
  const total = directDependents + indirectDependents;
  const segments = [...countsByType.entries()]
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a);
  const segmentTotal = segments.reduce((sum, [, count]) => sum + count, 0);

  return (
    <Column gap={2}>
      <Row align="flex-end" justify="space-between" gap={6} flexWrap="wrap">
        <SummaryMetric
          label="Total usage"
          value={total.toLocaleString()}
          isEmpty={total === 0}
          subtitle={
            lastCalculatedAt && (
              <Text size="sm" variant="monospace" color="text.secondary">
                Last calculated{" "}
                <Tooltip
                  message={`${formatDateLong(lastCalculatedAt)} at ${formatTime(lastCalculatedAt)}`}
                >
                  <Box as="span">
                    {formatDistanceToNow(lastCalculatedAt, { addSuffix: true })}
                  </Box>
                </Tooltip>
              </Text>
            )
          }
        />
        <Row align="flex-end" gap={6}>
          <SummaryMetric
            size="md"
            label="Direct"
            value={directDependents.toLocaleString()}
            isEmpty={directDependents === 0}
            valueTooltip="Resources that reference this one directly."
          />
          <SummaryMetric
            size="md"
            label="Indirect"
            value={indirectDependents.toLocaleString()}
            isEmpty={indirectDependents === 0}
            valueTooltip="Resources that reach this one through another resource."
          />
        </Row>
      </Row>

      {segmentTotal > 0 && (
        <Column gap={4}>
          <Row
            height={4}
            borderRadius="sm"
            overflow="hidden"
            gap="1px"
            aria-hidden
          >
            {segments.map(([resourceType, count]) => {
              const display = getDisplayNameForResource(resourceType);
              return (
                <Tooltip
                  key={resourceType}
                  message={`${display.pluralLabel} ${count.toLocaleString()}`}
                >
                  <Box
                    // Proportional to the subtree, not to the rows loaded.
                    flex={count}
                    bg={barColor(display.color)}
                  />
                </Tooltip>
              );
            })}
          </Row>
          <Column gap={1}>
            <Text id={typeFilterLabelId} color="text.secondary">
              Filter Loaded Resources
            </Text>
            <Row
              gap={2}
              flexWrap="wrap"
              role="group"
              aria-labelledby={typeFilterLabelId}
            >
              {segments.map(([resourceType, count]) => {
                const display = getDisplayNameForResource(resourceType);
                return (
                  <ToggleButton
                    key={resourceType}
                    size="sm"
                    icon={display.icon}
                    iconColor={barColor(display.color)}
                    label={`${display.pluralLabel} ${count.toLocaleString()}`}
                    isSelected={selectedTypes.has(resourceType)}
                    onChange={() => onToggleType(resourceType)}
                  />
                );
              })}
            </Row>
          </Column>
        </Column>
      )}
    </Column>
  );
};

type SummaryMetricProps = {
  label: string;
  value: string;
  size?: "md" | "lg";
  subtitle?: ReactNode;
  valueTooltip?: string;
  isEmpty?: boolean;
};

const SummaryMetric: FC<SummaryMetricProps> = ({
  label,
  value,
  size = "lg",
  subtitle,
  valueTooltip,
  isEmpty = false,
}) => {
  const valueText = (
    // A metric is a display number, not a section title, so it borrows the
    // heading type scale without taking heading semantics.
    <Heading
      as="span"
      size={VALUE_HEADING_SIZES[size]}
      variant="monospace"
      color={isEmpty ? "text.disabled" : "text.primary"}
    >
      {value}
    </Heading>
  );

  return (
    <Column flexShrink={0}>
      <Text
        size="sm"
        fontWeight="medium"
        variant="monospace"
        color="text.secondary"
      >
        {label}
      </Text>
      {valueTooltip ? (
        <Tooltip message={valueTooltip}>{valueText}</Tooltip>
      ) : (
        valueText
      )}
      {subtitle}
    </Column>
  );
};
